import React, { useState } from 'react';
import Modal from '$/components/global/modal';
import BixButton from '$/components/global/bixButton';
import { TranscriptionsAdminService } from '$/services';
import * as Bixby from '$/core';

export interface IDeleteTranscriptionModalProps {
  transcription: Bixby.Models.AdminCompanyTranscription | undefined;
  isOpen: boolean;
  onClose: () => void;
  onDeleted: () => void;
  dispatchToastError?: (message: string) => Promise<void>;
  dispatchToastSuccess?: (message: string) => Promise<void>;
}

const DeleteTranscriptionModal: React.FC<IDeleteTranscriptionModalProps> = ({
  transcription,
  isOpen,
  onClose,
  onDeleted,
  dispatchToastError,
  dispatchToastSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  if (!transcription) return null;

  const handleDelete = async () => {
    setError(undefined);
    setIsLoading(true);

    try {
      const result = await TranscriptionsAdminService.deleteTranscriptionAndCreateReuploadTaskAsync(
        transcription.transcriptionId
      );

      if (result.plannerTaskCreated === false) {
        setError('An error occurred while creating planner task. Please try again.');
        if (dispatchToastError) {
          await dispatchToastError('Could not create planner task.');
        }
      } else if (result.plannerTaskCreated === true && dispatchToastSuccess) {
        await dispatchToastSuccess('Transcript deleted. A new upload task has been created.');
      } else if (dispatchToastSuccess) {
        await dispatchToastSuccess('Transcript deleted.');
      }

      onClose();
      onDeleted();
    } catch {
      setError('An error occurred while deleting the transcript. Please try again.');
      if (dispatchToastError) {
        await dispatchToastError('Could not delete transcript.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(undefined);
      onClose();
    }
  };

  return (
    <Modal title="Delete Transcript" active={isOpen} onClose={handleClose}>
      <p>
        Are you sure you want to delete the transcript for <strong>{transcription.companyName}</strong>?
      </p>
      {error && <p className="error">{error}</p>}
      <div className="button_side_wrap">
        <BixButton type="button" onClick={handleDelete} disabled={isLoading} useLoading isLoading={isLoading}>
          Delete Transcript
        </BixButton>
        <BixButton light type="button" onClick={handleClose} disabled={isLoading}>
          Cancel
        </BixButton>
      </div>
    </Modal>
  );
};

export default DeleteTranscriptionModal;
